import { fetchWithAuth } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';

/**
 * Files API service
 */
export const FileApi = {
  /**
   * Get bucket size
   * @returns {Promise<Response>} Bucket size response
   */
  getBucketSize: async () => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/storage/bucket/size`, {
      method: 'GET',
    });
  },

  /**
   * Upload file
   * @param {File} file - File to upload
   * @param {Object} metadata - File metadata
   * @returns {Promise<Response>} Upload response
   */
  uploadFile: async (file, metadata = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    
    // Add metadata fields
    if (metadata.documentTypeId) formData.append('documentTypeId', metadata.documentTypeId);
    if (metadata.description) formData.append('description', metadata.description);
    if (metadata.endeavorId) formData.append('endeavorId', metadata.endeavorId);
    if (metadata.name) formData.append('name', metadata.name);
    
    // Add any additional metadata fields
    Object.keys(metadata).forEach(key => {
      if (!['documentTypeId', 'description', 'endeavorId', 'name'].includes(key)) {
        formData.append(key, metadata[key]);
      }
    });
    
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/storage/upload`, {
      method: 'POST',
      body: formData,
    });
  },

  /**
   * List files
   * @param {Object} options - Filter options (page, size, sort, etc.)
   * @returns {Promise<Response>} Files list response
   */
  listFiles: async (options = {}) => {
    const queryParams = new URLSearchParams(options).toString();
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/storage/list${queryParams ? '?' + queryParams : ''}`, {
      method: 'GET',
    });
  },

  /**
   * Delete file
   * @param {string|number} documentId - Document ID
   * @returns {Promise<Response>} Delete response
   */
  deleteFile: async (documentId) => {
    const queryParams = new URLSearchParams({ documentId }).toString();
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/storage/delete?${queryParams}`, {
      method: 'DELETE',
    });
  },

  /**
   * Download file directly - returns the file content as a blob
   * @param {string|number} documentId - Document ID
   * @param {number} expiration - Expiration time in seconds
   * @returns {Promise<Response>} File download response (blob)
   */
  downloadFile: async (documentId, expiration = 3600) => {
    const queryParams = new URLSearchParams({ documentId, expiration }).toString();
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/storage/direct-download?${queryParams}`, {
      method: 'GET',
    });
  },

  /**
   * Get download URL for file
   * @param {string|number} documentId - Document ID
   * @param {number} expiration - Expiration time in seconds
   * @returns {Promise<Response>} Download URL response
   */
  getDownloadUrl: async (documentId, expiration = 3600) => {
    const queryParams = new URLSearchParams({ documentId, expiration }).toString();
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/storage/download-url?${queryParams}`, {
      method: 'GET',
    });
  },

  /**
   * Upload multiple files
   * @param {FileList|Array} files - Files to upload
   * @param {Object} commonMetadata - Common metadata for all files
   * @returns {Promise<Array>} Array of upload responses
   */
  uploadMultipleFiles: async (files, commonMetadata = {}) => {
    const uploadPromises = Array.from(files).map(file => 
      FileApi.uploadFile(file, commonMetadata)
    );
    
    return Promise.all(uploadPromises);
  },

  /**
   * Get file metadata
   * @param {string|number} documentId - Document ID
   * @returns {Promise<Response>} File metadata response
   */
  getFileMetadata: async (documentId) => {
    const queryParams = new URLSearchParams({ documentId }).toString();
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/storage/metadata?${queryParams}`, {
      method: 'GET',
    });
  }
};